<template>
	<StandardModal
		open
		:title="$t('chat_reactions_label')"
		@cancel="$emit('cancel')"
		ref="modal"
		:tabOrder="[
			{
				css: '.pronto-input',
				clickOnEnter: true,
				Enter: () => $refs.search?.focus?.(),
			},
			{ all: '.tabs .tab-item', clickOnEnter: true },
			{ css: '.modal-footer-left .pronto-btn', clickOnEnter: true },
		]"
	>
		<div class="reactions-modal">
			<div class="filtering">
				<Input
					:placeholder="$t('admin_people_search_action')"
					class="search"
					v-model="query"
					:icon="SearchIcon"
					allowClear
					ref="search"
				/>
			</div>

			<Tabs
				alignLeft
				buttons
				small
				:tabs="tabs"
				:defaultSelected="defaultTab"
				@change="onChangeTab"
				v-if="!query"
				class="reactions-tabs"
				ref="table"
			>
				<template v-slot="{ tab }">
					<span class="reaction-tab-content">
						<span
							v-if="tab.label"
							class="reaction-tab-label"
							v-text="tab.label"
						/>
						<span class="reaction-tab-count" v-text="tab.count" />
					</span>
				</template>
			</Tabs>

			<Table
				:columns="columns"
				:rows="rows"
				:loadingMore="loadingMore"
				:loadingOverlay="loading"
				keyField="id"
				infinite
				loadMoreTrigger="0px"
				hideHeader
				clickable
				@row-click="openUserProfile"
				@load-more="onLoadMore"
				ref="table"
				:height="query ? '463px' : '394px'"
			>
				<template #cell-user="{ data }">
					<div class="user-row">
						<div class="user-data">
							<ProfilePic :user="data.user" size="40px" />
							<UserName :user="data.user" />
						</div>
						<div class="reactions">
							<span
								class="reaction-emoji"
								v-for="native in data.reactions"
								:key="native"
								v-text="native"
							/>
						</div>
					</div>
				</template>

				<template #empty>
					<NoSearchResults :query="query" />
				</template>
			</Table>
		</div>
		<template #footer-left>
			<Button link @click="refresh">
				<ArrowsRotateIcon type="regular" class="refresh-icon" />
				{{ $t('common_refresh') }}
			</Button>
		</template>
		<template #footer-right>
			<Button primary @click="$emit('cancel')">
				{{ $t('common_button_done') }}
			</Button>
		</template>
	</StandardModal>
</template>

<script>
import StandardModal from '@/components/common/StandardModal'
import {
	Button,
	Input,
	Tabs,
	ProfilePic,
	Table,
	UserName,
} from '@/components/common'
import { SearchIcon, ArrowsRotateIcon } from '@/icons'
import { supportedReactions } from '@/helpers/constants'
import { get } from '@/api/rest'
import { debounce } from 'debounce'
import { NoSearchResults } from '@/components/common'

export const PAGE_SIZE = 30

const nativeEmojisById = supportedReactions.reduce((acc, r) => {
	acc[r.id] = r.native
	return acc
}, {})

export default {
	name: 'ReactionsModal',
	components: {
		StandardModal,
		Button,
		Input,
		Tabs,
		SearchIcon,
		ProfilePic,
		ArrowsRotateIcon,
		Table,
		UserName,
		NoSearchResults,
	},
	props: {
		message: Object,
		defaultTab: [String, Number],
	},
	data() {
		return {
			query: '',
			SearchIcon,
			reactions: [],
			currentTab: this.defaultTab || null,
			columns: [{ field: 'user' }],
			reactionCounts: {},
			loadingMore: false,
			loadingReactions: true,
			loadingCounts: true,
		}
	},

	emits: ['cancel'],

	mounted() {
		this.fetchDebounced = debounce(this.fetchReactions, 500)
		this.refresh()
	},

	computed: {
		tabs() {
			const tabs = Object.entries(this.reactionCounts).map(([emoji_base, data]) => ({
				id: emoji_base,
				label: emoji_base,
				count: this.loadingCounts ? '-' : data.count ?? 0,
			}))

			if (tabs.length > 1) {
				tabs.unshift({ id: 'all', count: 'All' })
			}
			return tabs
		},
		rows() {
			return this.reactions.map(({ user, emojis }) => ({
				id: user.id,
				user,
				reactions: emojis
			}))
		},
		loading() {
			return this.loadingReactions || this.loadingCounts
		},
	},

	watch: {
		currentTab() {
			this.fetchReactions()
		},
		query() {
			this.loadingReactions = true
			this.fetchDebounced()
		},
	},

	methods: {
		onChangeTab({ id }) {
			this.currentTab = id
		},

		openUserProfile({ user }) {
			EventBus.$emit('openProfileCard', user)
		},

		async onLoadMore() {
			if (!this.apiResponse?.next) return
			try {
				this.loadingMore = true
				this.apiResponse = await this.apiResponse.next()
				this.reactions = this.reactions.concat(this.apiResponse.data)
			} catch (e) {
				console.error(e)
				this.$refs.modal.$alert.error(this.$t('common_api_error_save'))
			}
			this.loadingMore = false
		},

		refresh() {
			this.fetchCounts()
			this.fetchReactions()
		},

		async fetchCounts() {
			try {
				this.loadingCounts = true
				const resp = await get(`/messages/${this.message.id}/reactions/counts`)
				this.reactionCounts = resp.data.reduce((acc, r) => {
					if (!acc[r.emoji_base]) {
						// If this is the first time seeing this emoji_base, create a new entry
						acc[r.emoji_base] = {
							emoji_base: r.emoji_base,
							count: r.count
						}
					} else {
						// If we've seen this emoji_base before, add to its count
						acc[r.emoji_base].count += r.count
					}
					return acc
				}, {})
			} catch (e) {
				console.error(e)
			}
			this.loadingCounts = false
		},

		async fetchReactions() {
			try {
				this.loadingReactions = true
				const params = { 'page[size]': PAGE_SIZE }

				if (this.query) {
					params['filter[search]'] = this.query
				} else if (this.currentTab && this.currentTab !== 'all') {
					params['filter[emoji_base]'] = this.currentTab
				}

				this.apiResponse = await get(
					`/messages/${this.message.id}/reactions`,
					params
				)
				this.reactions = this.apiResponse.data
				this.$refs.table?.scrollToTop?.()
			} catch (e) {
				console.error(e)
				this.$refs.modal.$alert.error(this.$t('common_api_error_save'))
			}
			this.loadingReactions = false
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.reactions-modal {
	display: flex;
	flex-direction: column;

	.filtering {
		border-bottom: 1px solid var(--neutral-200);
		padding: 14px 22px;
	}

	:deep(.table-row) {
		.table-cell {
			.table-cell-content {
				display: block !important;
			}
		}
	}

	.reactions-tabs {
		overflow-x: scroll;

		:deep(.tab-item) {
			width: 65px !important;
		}
		.reaction-tab-content {
			@include flex-center(8px);
			width: 100%;
			.reaction-tab-label {
				font-size: 18px;
			}
			.reaction-tab-count {
				font-size: 13px;
				height: 16px;
			}
		}
	}

	.user-row {
		@include flex-spread;
		width: 100%;
		.user-data {
			@include flex(8px);
			.profile-pic-avatar {
				flex-shrink: 0;
			}
		}
		.reactions {
			@include flex(8px);
			font-size: 24px;
			flex-wrap: wrap;
			justify-content: flex-end;
			max-width: 183px;
			padding: 4px 0;

			.reaction-emoji {
				line-height: 1;
			}

			.name {
				max-width: 400px;
				@include ellipsis;
				margin-left: 12px;
			}
		}
	}

	:deep(.tabs-container) {
		.tab-item {
			padding: 21px;
			font-family: Avenir-Heavy;
			font-size: 17px;
		}
	}
}

:deep(.refresh-icon) {
	margin-right: 8px;
}
</style>
